/*
 * Copyright © 2018-2025, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import moment from "moment";
import { strings } from "../../../../common/Strings/Strings";
import { PageProps } from "../../../../models/PageProps";
import { Loader } from "../../../../components/Loader";
import Modal from "../../../../components/Modal/Modal";
import Button from "../../../../components/Button";
import { PetDetailsResponse } from "../../../../models/pet/PetDetailsResponse";
import SelectTime from "./SelectTime";
import FinishReservation from "./FinishReservation";
import CloseButton from "../../../../components/CloseButton";
import UserPetApi from "../../../../api/UserPetApi";
import { getGeneralError, localDateFormat } from "../../../../util/helperFunctions";
import AlertBox from "../../../../components/AlertBox";
import { ReservationTypeResponse } from "../../../../models/reservationtype/ReservationTypeResponse";
import { ClinicProfileResponse } from "../../../../models/clinic/profile/ClinicProfileResponse";
import { Appointment } from "../../../../models/calendar/Appointment";
import ReservationCreationApi from "../../../../api/ReservationCreationApi";
import { NewUserReservationRequest } from "../../../../models/reservation/NewUserReservationRequest";
import AppointmentDetails from "../Appointments/AppointmentDetails";

interface CreateReservationForm {
  collabId?: string;
  pet: PetDetailsResponse[] | undefined;
  description: string;
  reservationPeriod: Appointment;
  userInformedAboutGdpr: boolean;
  videoConsultation: "inPerson" | "video";
  vet: "anyVet" | "specificVet";
}

type Params = {
  userPetId?: string;
};

const ReservationDetails: React.FC<PageProps> = ({ setPageLoading }: PageProps) => {
  const { userPetId } = useParams<Params>();
  const [step, setStep] = useState<"time" | "details">("time");
  const [error, setError] = useState<string | null>(null);
  const [modalIsOpen, setModalOpen] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();
  const [reservationType] = useState<ReservationTypeResponse | undefined>(location?.state?.resType);
  const [clinic] = useState<ClinicProfileResponse | undefined>(location?.state?.clinic);
  const [loading, setLoading] = useState<boolean>(false);

  const openModal = () => {
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const methods = useForm<CreateReservationForm>({
    mode: "onSubmit",
    defaultValues: {
      collabId: undefined,
      pet: undefined,
      description: "",
      userInformedAboutGdpr: false,
      vet: "anyVet",
      videoConsultation: "inPerson",
    },
  });

  const {
    getValues,
    handleSubmit,
    formState: { errors },
    setValue,
    trigger,
    watch,
  } = methods;
  const [reservationPeriod, pet] = watch(["reservationPeriod", "pet"]);

  useEffect(() => {
    const fetchPet = async () => {
      if (userPetId) {
        try {
          const resp = await UserPetApi.getUserPet(userPetId);
          setValue("pet", [resp.data.userPetDetails]);
        } catch (e) {
          setError(await getGeneralError(e));
        } finally {
          setPageLoading(false);
        }
      } else {
        setPageLoading(false);
      }
    };

    if (userPetId !== "undefined") {
      void fetchPet();
    } else {
      setPageLoading(false);
    }
  }, [setPageLoading, userPetId, setValue]);

  const submit = async (formData: CreateReservationForm) => {
    const { reservationPeriod, videoConsultation } = formData;
    setLoading(true);
    setError(null);

    if (pet && reservationPeriod && reservationPeriod.shiftId && reservationPeriod && reservationType && clinic) {
      const start = moment(reservationPeriod.start).format(localDateFormat());
      const end = moment(reservationPeriod.end).format(localDateFormat());

      const reservation: NewUserReservationRequest = {
        petId: pet?.[0]?.id,
        timePeriod: { startTime: start, endTime: end },
        reservationTypeId: reservationType?.id,
        description: formData.description,
        isVideoConsultation: videoConsultation === "video",
      };

      try {
        await ReservationCreationApi.createUserReservation(clinic?.id, reservationPeriod.shiftId, reservation);
        navigate("/appointments");
      } catch (err) {
        setError(await getGeneralError(err));
      } finally {
        closeModal();
        setLoading(false);
      }
    }
  };

  return (
    <main className="main-signed-in">
      <section>
        <div className="flex flex-col items-center justify-center space-y-6 mx-auto px-4 md:px-0 py-10 md:py-14">
          <div className="w-full md:max-w-lg md:tw-card space-y-6 md:p-8">
            <div className="flex justify-between space-x-4">
              <div>
                <h1 className="text-xl font-semibold leading-tight text-zinc-800 lg:text-2xl dark:text-white">
                  {strings.addAppointment}
                </h1>
              </div>
              <span className="text-gray-500 dark:text-gray-400">{step === "time" ? 3 : 4}/4</span>
            </div>
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(submit)} className="space-y-6" id="createReservationForm">
                <AlertBox message={error} />
                {step === "time" ? (
                  <SelectTime reservationType={reservationType} clinic={clinic} />
                ) : (
                  <FinishReservation />
                )}
                <AlertBox hidden={!errors?.userInformedAboutGdpr} message={strings.appointmentErrorGDPR} />
                <AlertBox hidden={!errors?.reservationPeriod} message={strings.appointmentErrorTimeAndDate} />
                <div className="w-full">
                  <Button
                    variant="primary"
                    type="button"
                    onClick={() => {
                      if (step === "time") {
                        const response = trigger(["videoConsultation", "vet", "reservationPeriod"]);

                        response.then((isValid: boolean) => {
                          if (isValid) {
                            setStep("details");
                          }
                        });
                      } else {
                        const response = trigger(["pet", "description", "userInformedAboutGdpr"]);

                        response.then((isValid: boolean) => {
                          if (isValid) {
                            openModal();
                          }
                        });
                      }
                    }}
                  >
                    {strings.continue}
                  </Button>
                </div>
                <Button
                  onClick={() => {
                    if (step === "details") {
                      setStep("time");
                    } else {
                      navigate(-1);
                    }
                  }}
                  variant="link"
                >
                  {strings.back}
                </Button>
                <Modal show={modalIsOpen} handleClose={closeModal}>
                  <Modal.Header title={strings.addAppointment} />
                  <Modal.Body>
                    {reservationPeriod && clinic ? (
                      <AppointmentDetails
                        appointmentEnd={reservationPeriod.end}
                        appointmentStart={reservationPeriod.start}
                        clinicAddress={clinic.addresses?.[0]}
                        clinicId={clinic.id}
                        clinicName={clinic.name}
                        description={getValues("description")}
                        isVideoConsultation={getValues("videoConsultation") === "video"}
                        resType={reservationType}
                      />
                    ) : (
                      <></>
                    )}
                  </Modal.Body>
                  <Modal.Footer>
                    <CloseButton onClick={closeModal} />
                    <Button
                      disabled={loading}
                      loading={loading}
                      className="modal-main-button"
                      type="submit"
                      form="createReservationForm"
                      variant="primary"
                    >
                      {strings.reserve}
                    </Button>
                  </Modal.Footer>
                </Modal>
              </form>
            </FormProvider>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Loader(ReservationDetails);
